<!--@format-->
<template>
    <div class="invoice-details-widget">
        <loading-card :is-loading="isLoading" />
        <div v-if="!isLoading" class="widget-contents-container">
            <div class="widget-header">
                <div class="header-text">
                    <h3>{{ widgetTitle }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <invoice-details :details="detailsData" />
                <div class="actions-container">
                    <app-button class="secondary-btn" @click="handleViewInvoice">
                        View Invoice
                    </app-button>
                    <app-button
                        :loading="loadingDownload"
                        class="print-btn"
                        @click="handlePrintDetails"
                    >
                        Print <k26-print-outline-svg />
                    </app-button>
                </div>
                <div v-if="!paidInGlasshive" class="document-container">
                    <div class="file-upload-container">
                        <p class="file-upload-description">
                            {{
                                isPaidWithProof
                                    ? 'Uploaded document'
                                    : 'Would you like to attach proof of payment?'
                            }}
                        </p>
                        <app-files-preview v-if="isPaidWithProof" :files="[proofOfPayment]">
                            <template slot="actions">
                                <app-icon-button class="edit-action" @click="handleEditProof">
                                    <t25-modify-email-svg color="#18aeff" />
                                </app-icon-button>
                                <app-icon-button class="delete-action" @click="handleDeleteFile">
                                    <e25-deleted-items-svg color="#f30070" />
                                </app-icon-button>
                            </template>
                        </app-files-preview>
                        <div v-else>
                            <app-drag-and-drop-files
                                files-title=""
                                label="Or Drag File Here"
                                library-enabled
                                hide-area-on-upload
                                :files="proofOfPayment.file ? [proofOfPayment] : []"
                                :file-accept="fileConfig.fileSelectorAllowedTypes"
                                :library-file-types="fileConfig.fileSelectorAllowedTypes"
                                :library-multiple="false"
                                :loading-files="loadingFiles"
                                @upload="handleAddFile"
                                @remove="handleDeleteFile"
                                @library:selected="handleCollateralSelected"
                            />
                            <p v-if="!proofOfPayment.file" class="file-max-size">
                                Maximum upload file size: <strong>100 MB</strong>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <app-button :loading="uploadingFiles" @click="handleProofUpload">Done</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import FileMixin, { FileByteSizeLimitError, FileTypeError } from '@/mixins/file-mixin';
import { FileService } from '@/scripts/services/file/file-service';
import { MDFInvoicesService } from '@/scripts/services/invoices/mdf-invoices-service.js';
import { InvoiceProofOfPaymentServiceMixin } from '@/mixins/invoice-proof-of-payment-mixin';
import { LogLevel } from '@/scripts/logger';

const AppButton = () => import('@/components/common/app-button');
const AppDragAndDropFiles = () => import('@/components/common/app-drag-and-drop-files');
const AppFilesPreview = () => import('@/components/common/app-files-preview');
const AppIconButton = () => import('@/components/common/app-icon-button');
const E25DeletedItemsSvg = () => import('@/components/svg/grid/e25-deleted-items-svg');
const K26PrintOutlinedSvg = () => import('@/components/svg/grid/k26-print-outline-svg');
const T25ModifyEmailSvg = () => import('@/components/svg/grid/t25-modify-email-svg');
const LoadingCard = () => import('@/components/common/loading-card');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const InvoiceDetails = () => import('@/components/invoice-template/invoice-details');
import { MdfInvoiceMixin } from '@/mixins/mdf-invoice-mixin';
import { FeeEnumMixin } from '@/mixins/fee-enum-mixin';

export default {
    name: 'invoice-details-widget',

    components: {
        'app-button': AppButton,
        'app-drag-and-drop-files': AppDragAndDropFiles,
        'app-files-preview': AppFilesPreview,
        'app-icon-button': AppIconButton,
        'e25-deleted-items-svg': E25DeletedItemsSvg,
        'k26-print-outline-svg': K26PrintOutlinedSvg,
        't25-modify-email-svg': T25ModifyEmailSvg,
        'loading-card': LoadingCard,
        'widget-close-svg': WidgetCloseSvg,
        'invoice-details': InvoiceDetails,
    },

    mixins: [FileMixin, InvoiceProofOfPaymentServiceMixin, MdfInvoiceMixin, FeeEnumMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            loadingFiles: false,

            uploadingFiles: false,

            widgetTitle: 'Proof of Payment',

            fileConfig: {
                byteSizeMinimum: 0,
                byteSizeLimit: 100000000,
                fileSelectorAllowedTypes: ['pdf'],
                allowedImageTypes: ['application/pdf'],
            },

            proofOfPayment: {
                file: null,
                name: null,
                size: null,
                type: null,
            },

            initialProofOfPayment: {
                file: null,
                name: null,
                size: null,
                type: null,
            },

            detailsData: [],

            paymentProcessing: '',

            isCollateral: false,
            invoiceDetails: {},
            loadingDownload: false,
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        invoiceId() {
            return this.widget.invoiceId;
        },

        accountId() {
            return this.widget.accountId;
        },

        proofChange() {
            return this.initialProofOfPayment.file !== this.proofOfPayment.file;
        },

        paidInGlasshive() {
            return this.paymentProcessing === 'Paid in GlassHive';
        },

        isPaidWithProof() {
            const isPaid = true;
            return isPaid && this.proofOfPayment.file && !this.proofChange;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },
    },

    created() {
        this.getInvoiceDetails().catch((error) => {
            this.$helpers.showFailNotification(
                'Error trying to get invoice details or proof of payment',
                {
                    error,
                },
            );
        });
        EventBus.$on('proof-of-payment:update', this.getProofOfPayment);
    },

    beforeDestroy() {
        EventBus.$off('proof-of-payment:update', this.getProofOfPayment);
    },

    methods: {
        formatDate(date) {
            const options = { month: 'short', day: 'numeric', year: 'numeric' };
            return new Date(date).toLocaleDateString('en-US', options);
        },

        getInvoiceDetails() {
            this.isLoading = true;
            return MDFInvoicesService.getInvoiceById(this.invoiceId, this.accountId)
                .then(async (response) => {
                    const data = response.data.data;
                    this.paymentProcessing = data.PaymentProcessing;
                    let billedTo = data.BilledTo.VendorName;
                    let paymentDetails = null;
                    this.proofOfPayment =
                        data.ProofOfPayment && data.ProofOfPayment.ProofUrl
                            ? {
                                  name: data.ProofOfPayment.FileName,
                                  file: data.ProofOfPayment.ProofUrl,
                                  size: data.ProofOfPayment.Size,
                                  type: data.ProofOfPayment.MimeType,
                                  downloadUrl: data.ProofOfPayment.ProofUrl,
                              }
                            : false;
                    this.initialProofOfPayment = { ...this.proofOfPayment };
                    if (data.PaymentMethodId) {
                        paymentDetails = await this.getBilledToDetailsMixin(
                            data.PaymentMethodId,
                            data.BilledTo.VendorName,
                            this.agencyId,
                        );
                        billedTo = paymentDetails.billedToDetails;
                    }

                    this.detailsData = [
                        [
                            {
                                title: 'Payable to',
                                content: data.PayableTo,
                            },
                            {
                                title: 'Invoice Number',
                                content: data.InvoiceNumber,
                            },
                            {
                                title: 'Transaction Date',
                                content: data.InvoiceIsued && this.formatDate(data.InvoiceIsued),
                            },
                            {
                                title: data.Items && data.Items.length > 1 ? 'Items' : 'Item',
                                content: data.Items.map((item) => item.FundTypeName).join(', '),
                            },
                            {
                                title: 'Payment Method',
                                content: data.PaymentProcessing,
                                contentDetails: paymentDetails && paymentDetails.cardDetails,
                                contentClass: 'capitalize',
                            },
                        ],
                        [
                            {
                                title: 'Base Fee',
                                content: `$${data.BaseFee}`,
                            },
                        ],
                        [
                            {
                                title: 'Total:',
                                content: `$${data.AmountPaid}`,
                                contentClass: 'total-amount',
                            },
                        ],
                    ];

                    if (this.paidInGlasshive) {
                        this.detailsData[1].push(
                            {
                                title: 'GlassHive Service Fee',
                                additionalTitleInfo: `(${this.feeEnum.mdfGlasshiveFee})`,
                                content: `$${data.ServiceFee || data.BaseFee * 0.025}`,
                            },
                            {
                                title: 'Processing Fees',
                                additionalTitleInfo: `(${this.feeEnum.mdfProcessingFee})`,
                                content: `$${data.ProcessingFee || data.BaseFee * 0.035}`,
                            },
                        );
                    }

                    this.invoiceDetails = {
                        invoiceMode: this.invoiceMode,
                        invoiceNumber: data.InvoiceNumber,
                        paymentDue: this.$moment.utc(data.DueDate).local(),
                        invoiceIssued: this.$moment.utc(data.InvoiceIsued).local(),
                        paymentDate: this.$moment.utc(data.PaidOn).local(),

                        isPaid: data.Status === 'paid',
                        companyName: data.PayableTo,
                        purchased: `MDF - ${data.FundTitle}`,
                        confirmationNumber: data.ConfirmationNumber,
                        items: data.Items,

                        paymentMethod: paymentDetails,
                        baseFee: data.BaseFee && data.BaseFee,
                        glasshiveFee: data.ServiceFee && data.ServiceFee,
                        salesTax: data.SalesTax || data.SalesTax,
                        processingFee: data.ProcessingFee && data.ProcessingFee,
                        amount: data.AmountPaid,
                        address: billedTo,
                        glasshiveAddress: '900 W Bethany Dr, Suite 240 Allen, TX 75013',
                        proofOfPayment: [this.proofOfPayment],
                        isMarkedAsPaid:
                            data.Status === 'paid' && data.PaymentMethod === 'Marked as Paid',
                        fundTitle: data.FundTitle,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error trying to get invoice details', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getProofOfPayment() {
            this.isLoading = true;
            return this.dispatchProofOfPayment({
                invoiceId: this.invoiceId,
                accountId: this.accountId,
            })
                .then((data) => {
                    this.proofOfPayment = {
                        file: data.ProofUrl,
                        name: data.FileName,
                        size: data.FileSize,
                        type: data.FileMimeType,
                    };
                    this.initialProofOfPayment = { ...this.proofOfPayment };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Error trying to get your proof of payment',
                        {
                            error,
                        },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleViewInvoice() {
            this.closeWidget();
            this.$router.push(
                `${this.baseUrl}/Account/Billing?id=${this.invoiceId}&submenu=mdf#Invoices`,
            );
        },

        handlePrintDetails() {
            this.loadingDownload = true;

            this.printMdfInvoicePdf(this.invoiceDetails).then(() => {
                this.loadingDownload = false;
            });
        },

        handleAddFile(e) {
            this.loadingFiles = true;
            this.getBase64Url(e, this.fileConfig)
                .then(({ encodingStr, name, size, type }) => {
                    this.isCollateral = false;
                    this.proofOfPayment = {
                        file: encodingStr,
                        name,
                        size,
                        type,
                    };
                })
                .catch((error) => {
                    if (error instanceof FileByteSizeLimitError) {
                        this.$helpers.showFailNotification(
                            'File size exceeds the maximum amount of 100Mbs',
                            { level: LogLevel.INFO },
                        );
                    } else if (error instanceof FileTypeError) {
                        const [head, ...tail] = this.fileConfig.fileSelectorAllowedTypes;
                        this.$helpers.showFailNotification(
                            `File needs to be of type ${tail.join(', ')} or ${head}`,
                            { level: LogLevel.INFO },
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your file. Please check your format and try again.',
                            { error },
                        );
                    }
                })
                .finally(() => {
                    this.loadingFiles = false;
                });
        },

        handleCollateralSelected(file) {
            const [selectedFile] = file;
            this.isCollateral = true;
            this.proofOfPayment = {
                file: selectedFile.url,
                name: selectedFile.previewName,
                size: selectedFile.size,
                type: selectedFile.type,
            };
        },

        handleProofUpload() {
            this.uploadingFiles = true;
            if (this.proofChange) {
                if (!this.proofOfPayment.file) {
                    return this.handleDeleteProof();
                }
                if (this.isCollateral) {
                    const proofPayload = {
                        accountId: this.accountId,
                        file: this.proofOfPayment,
                        invoiceId: this.invoiceId,
                    };
                    return this.handleUploadProofOfPayment(proofPayload)
                        .then(() => {
                            this.closeWidget();
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification(
                                'Error trying to save proof of payment',
                                {
                                    error,
                                },
                            );
                        })
                        .finally(() => {
                            this.uploadingFiles = false;
                        });
                }
                const filePayload = {
                    name: this.proofOfPayment.name,
                    url: this.proofOfPayment.file,
                };
                return FileService.post(filePayload)
                    .then((response) => {
                        this.proofOfPayment.file = response.data;
                        const proofPayload = {
                            accountId: this.accountId,
                            file: this.proofOfPayment,
                            invoiceId: this.invoiceId,
                        };
                        return this.handleUploadProofOfPayment(proofPayload);
                    })
                    .then(() => {
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Error trying to save proof of payment',
                            {
                                error,
                            },
                        );
                    })
                    .finally(() => {
                        this.uploadingFiles = false;
                    });
            }
            this.closeWidget();
        },

        handleDeleteFile() {
            this.proofOfPayment = {
                file: null,
                name: null,
                size: null,
                type: null,
            };
        },

        handleEditProof() {
            EventBus.$emit('open-widget', {
                name: 'edit-proof-of-payment-widget',
                options: {
                    title: 'Edit',
                    invoiceId: this.invoiceId,
                    accountId: this.accountId,
                },
            });
        },

        handleDeleteProof() {
            this.deleteProofOfPayment({
                accountId: this.accountId,
                invoiceId: this.invoiceId,
            })
                .then(() => {
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error trying to delete proof of payment', {
                        error,
                    });
                })
                .finally(() => {
                    this.uploadingFiles = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.invoice-details-widget.widget .content-wrapper {
    padding-top: 25px;
}

.invoice-details-widget .actions-container {
    display: flex;
    flex-direction: row;
    gap: 9px;
    margin-top: 32px;
}

.invoice-details-widget .actions-container .app-button {
    width: 155px;
}

.invoice-details-widget .actions-container .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.invoice-details-widget .actions-container .secondary-btn:hover {
    background: #dbedfa;
}

.invoice-details-widget .actions-container .print-btn {
    display: flex;
    gap: 19px;
}

.invoice-details-widget .actions-container .print-btn .k26-print-outline-svg path {
    stroke: #ffffff;
}

.invoice-details-widget .document-container {
    margin-top: 30px;
}

.invoice-details-widget .document-container .file-container {
    align-items: center;
    border: 2px solid #e9e9ef;
    display: flex;
    flex-direction: row;
    height: 56px;
    justify-content: space-between;
    padding: 10px 12px;
    width: 250px;
}

.invoice-details-widget .file-container .file-details {
    display: flex;
    flex-direction: row;
    gap: 8px;
}

.invoice-details-widget .file-container .app-file-icon {
    width: 36px;
}

.invoice-details-widget .file-container .file-remove-icon {
    width: 10px;
}

.invoice-details-widget .file-container .file-remove-icon .a {
    stroke: #f30070;
}

.invoice-details-widget .file-upload-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.invoice-details-widget .file-upload-container svg {
    height: auto;
    width: 24px;
}

.invoice-details-widget .file-upload-container .delete-action:hover {
    background-color: #f5d5e7;
}

.invoice-details-widget .file-upload-container .file-upload-description {
    font: normal normal 500 16px Roboto;
}

.invoice-details-widget .file-upload-container .file-max-size,
.invoice-details-widget .file-upload-container .file-max-size strong {
    color: #877d88;
}

.invoice-details-widget .app-files-preview .file-preview {
    min-width: 262px;
    width: fit-content;
}

.invoice-details-widget.widget .widget-footer {
    justify-content: flex-end;
}

.invoice-details-widget .widget-footer .app-button {
    width: 174px;
}
</style>
